import {
  PREDICTIONS_FAILURE,
  PREDICTIONS_PROCESSING,
  PREDICTIONS_SUCCESS,
} from '@common/constants/webSockets'
import { IOngoingReportingRawMappingData } from '@common/interfaces/bbc'
import omit from 'lodash/omit'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { IAction } from '../apiMiddleware'
import { LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS } from '../ongoingReporting/actions'
import * as actions from './actions'
import { predictionKeyOf } from './predictionKey'

export interface IPredictionState {
  isLoading: boolean
  progress: number
  total: number
  current: number
  /** The run stopped on its final attempt and won't report again. */
  isFailed?: boolean
  /** The last run to end here, so a stale "still pending" answer about it isn't read as the next run. */
  settledRunId?: string
  /** The run a seed from the consolidated response is waiting on; recognisable while still loading. */
  seededRunId?: string
  /** When this screen first heard the run: an empty queue soon after is its pending enqueue, later a lost ending. */
  heardAt?: number
  /** How many runs have ended here. Screens refresh on each: overlapping runs end at different times. */
  settledRuns?: number
  /** Bank Transactions only — groups the last completed run left for a later pass. */
  deferredGroups?: number
  /** Wires / MidDayWires only — memos the last completed run left for a later pass. */
  deferredMemos?: number
  /** Bank Transactions and wires — whether a follow-up pass was queued for what was left. */
  isContinuing?: boolean
}

/** How long after a run is first heard an empty queue may still be its pending enqueue. */
const ANNOUNCE_WINDOW_MS = 10_000

const predictionsInitialState: Record<string, IPredictionState> = {}

interface IPredictionEvent {
  reportId: string
  reportType: string
  progress: number
  total: number
  current: number
  /** Absent on the announce that precedes an enqueue: there is no job yet. */
  runId?: string
  deferredGroups?: number
  deferredMemos?: number
  isContinuing?: boolean
}

/** A run ended: stop loading, count it, and remember which run it was where the event says. */
const settled = (
  previous: IPredictionState | undefined,
  runId: string | undefined,
): IPredictionState => ({
  ...previous,
  settledRunId: runId ?? previous?.settledRunId,
  seededRunId: undefined,
  heardAt: undefined,
  settledRuns: (previous?.settledRuns ?? 0) + 1,
  isLoading: false,
})

/**
 * Seed a run already in flight when the screen opened, since its events reach only
 * connected clients. Keyed by run id: a response can land after the completion it predates.
 */
const seedFromConsolidated = (
  state: Record<string, IPredictionState>,
  { predictions }: Partial<IOngoingReportingRawMappingData>,
  receivedAt: number | undefined,
): Record<string, IPredictionState> => {
  if (!predictions) {
    return state
  }
  const key = predictionKeyOf(predictions.reportId, predictions.statementType)
  const known = state[key]

  if (!predictions.runId) {
    // Nothing queued: a seed is taken back, and a live run past the announce window ended unheard.
    if (!known?.isLoading) {
      return state
    }
    if (known.seededRunId) {
      return omit(state, key)
    }
    // An answer with no arrival time can't be placed against the window, so it settles nothing.
    return receivedAt !== undefined && receivedAt - (known.heardAt ?? 0) > ANNOUNCE_WINDOW_MS
      ? { ...state, [key]: settled(known, undefined) }
      : state
  }
  // A live entry is the fresher truth, and a run is announced just before it is enqueued.
  if (known?.isLoading) {
    return state
  }
  // This run already ended here; re-seeding would restart a settled bar.
  if (known?.settledRunId === predictions.runId) {
    return state
  }
  return {
    ...state,
    [key]: {
      isLoading: true,
      progress: 0,
      total: 0,
      current: 0,
      settledRunId: known?.settledRunId,
      settledRuns: known?.settledRuns,
      seededRunId: predictions.runId,
    },
  }
}

// Return type stated rather than inferred: the branches build different shapes,
// and an inferred union hides fields behind a narrowing consumers should not need.
function predictionsReducer(
  state: Record<string, IPredictionState> = predictionsInitialState,
  action: IAction,
): Record<string, IPredictionState> {
  switch (action.type) {
    case PREDICTIONS_PROCESSING: {
      const data = action.data as IPredictionEvent
      const key = predictionKeyOf(data.reportId, data.reportType)
      return {
        ...state,
        [key]: {
          isLoading: true,
          progress: data.progress,
          total: data.total,
          current: data.current,
          // Carried over, since the announce names no run; a seed is superseded by a real run.
          settledRunId: state[key]?.settledRunId,
          settledRuns: state[key]?.settledRuns,
          heardAt: state[key]?.isLoading ? state[key]?.heardAt : action.receivedAt,
        },
      }
    }
    case PREDICTIONS_SUCCESS: {
      const success = action.data as IPredictionEvent
      const key = predictionKeyOf(success.reportId, success.reportType)
      return {
        ...state,
        [key]: {
          // A retraction names no run, so the id of the run that ended is kept.
          ...settled(state[key], success.runId),
          progress: 100,
          deferredGroups: success.deferredGroups,
          deferredMemos: success.deferredMemos,
          isContinuing: success.isContinuing,
          // Cleared explicitly: a failed attempt can precede a successful retry.
          isFailed: false,
        },
      }
    }
    case PREDICTIONS_FAILURE: {
      // Not 100%: `isFailed` lets the screen say the run didn't finish, unlike "no suggestions".
      const failure = action.data as IPredictionEvent
      const key = predictionKeyOf(failure.reportId, failure.reportType)
      return {
        ...state,
        [key]: {
          progress: 0,
          total: 0,
          current: 0,
          ...settled(state[key], failure.runId),
          isFailed: true,
        },
      }
    }
    case LIST_CONSOLIDATED_ONGOING_REPORTING_SUCCESS:
      return seedFromConsolidated(
        state,
        (action.data || {}) as Partial<IOngoingReportingRawMappingData>,
        action.receivedAt,
      )
    default:
      return state
  }
}

export default combineReducers({
  loadingArray: loadingArrayReducer([actions.prefix]),
  predictions: predictionsReducer,
})
